import React, { forwardRef } from 'react';
import { ExclamationCircleIcon, CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface Option {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface FormSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  success?: string;
  helperText?: string;
  required?: boolean;
  loading?: boolean;
  options: Option[];
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
}

const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(({
  label,
  error,
  success,
  helperText,
  required,
  loading,
  options,
  placeholder,
  size = 'md',
  variant = 'default',
  className = '',
  ...props
}, ref) => {
  const [focused, setFocused] = React.useState(false);

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2.5 text-sm',
    lg: 'px-4 py-3 text-base',
  };

  const variantClasses = {
    default: 'border border-gray-300 bg-white',
    filled: 'border-0 bg-gray-100',
    outlined: 'border-2 border-gray-300 bg-transparent',
  };

  const getStateClasses = () => {
    if (error) {
      return 'border-red-500 ring-red-500 focus:border-red-500 focus:ring-red-500';
    }
    if (success) {
      return 'border-green-500 ring-green-500 focus:border-green-500 focus:ring-green-500';
    }
    if (focused) {
      return 'border-[#008B8B] ring-[#008B8B] focus:border-[#008B8B] focus:ring-[#008B8B]';
    }
    return 'focus:border-[#008B8B] focus:ring-[#008B8B]';
  };

  const selectClasses = [
    'w-full rounded-lg transition-colors duration-200 appearance-none',
    'focus:outline-none focus:ring-2 focus:ring-opacity-50',
    'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
    'pr-10', // Space for chevron icon
    sizeClasses[size],
    variantClasses[variant],
    getStateClasses(),
    className,
  ].filter(Boolean).join(' ');

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
    setFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    setFocused(false);
    props.onBlur?.(e);
  };

  return (
    <div className="w-full">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Select Container */}
      <div className="relative">
        {/* Select */}
        <select
          ref={ref}
          className={selectClasses}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={loading}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Icons */}
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center space-x-1 pointer-events-none">
          {/* Loading Spinner */}
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-[#008B8B]"></div>
          )}

          {/* Success Icon */}
          {success && !loading && (
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          )}

          {/* Error Icon */}
          {error && !loading && (
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          )}

          {/* Chevron Icon */}
          {!loading && (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Helper Text / Error / Success Message */}
      {(error || success || helperText) && (
        <div className="mt-1 text-sm">
          {error && (
            <div className="text-red-600 flex items-center">
              <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              {typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}
            </div>
          )}
          {success && !error && (
            <div className="text-green-600 flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              {success}
            </div>
          )}
          {helperText && !error && !success && (
            <div className="text-gray-500">{helperText}</div>
          )}
        </div>
      )}
    </div>
  );
});

FormSelect.displayName = 'FormSelect';

export default FormSelect;